import type { IResponsive, IViewport } from "../../types";

type ICSSVariable = `--${string}`;
type IValue = string | boolean | number | undefined;

export const applyClass = (
  className: string,
  value: IValue,
  base?: boolean
): string | null => {
  if ((value === true || value === "") && base) return className;
  if (value === false && base) return null;

  if ((value === true || value === "") && !base) return `${className}-true`;
  if (value === false && !base) return `${className}-false`;

  if (value !== undefined) return `${className}-${value}`;
  return null;
};

export const responsiveClass = (
  className: string,
  value: IResponsive<IValue>
) => {
  if (typeof value !== "object") {
    const staticClassName = applyClass(className, value, true);
    return staticClassName ? [staticClassName] : [];
  }

  return Object.keys(value).reduce<string[]>((acc, key) => {
    const isBase = key === "xs";
    const viewportClassName = applyClass(
      className,
      value[key as IViewport],
      isBase
    );
    const suffix = isBase ? "" : `-${key}`;

    if (viewportClassName === null) return acc;
    return [...acc, `${viewportClassName}${suffix}`];
  }, []);
};

export const responsiveVariables = <V extends IValue = IValue>(
  variableName: ICSSVariable,
  value: IResponsive<V>
): Record<ICSSVariable, V> => {
  if (value === undefined || value === "") return {};

  if (typeof value !== "object") {
    return { [variableName]: value };
  }

  return Object.keys(value).reduce<Record<ICSSVariable, V>>((acc, key) => {
    const isBase = key === "xs";
    const suffix = isBase ? "" : `-${key}`;
    const viewportValue = value[key as IViewport];

    if (viewportValue === undefined) return acc;
    if (viewportValue === false) return acc;
    if (viewportValue === "") return acc;
    return {
      ...acc,
      [`${variableName}${suffix}`]: viewportValue,
    };
  }, {});
};

export const responsiveDSVariables = <V extends string = string>(
  variableName: ICSSVariable,
  value: IResponsive<V>,
  suffix = "--pl-"
): Record<ICSSVariable, string> => {
  let formattedValue;
  if (typeof value !== "object") {
    if (value === undefined || value === "") return {};
    formattedValue = `var(${suffix}${value})`;
  } else {
    formattedValue = Object.keys(value).reduce<Record<ICSSVariable, string>>(
      (acc, key) => {
        const viewportValue = value[key as IViewport];

        if (viewportValue === undefined) return acc;
        if (viewportValue === "") return acc;
        return {
          ...acc,
          [`${key}`]: `var(${suffix}${viewportValue})`,
        };
      },
      {}
    );
  }

  return responsiveVariables<string>(variableName, formattedValue);
};
